import countries from 'i18n-iso-countries';
import parsePhoneNumberFromString from 'libphonenumber-js/core';
import metadata from 'libphonenumber-js/metadata.max';
import { z } from 'zod';

export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const capitalizeWords = (str: string) =>
  str.toLowerCase().replace(/(^|[\s'-])\p{L}/gu, (char) => char.toUpperCase());

export const formatCountry = (countryName: string) => {
  try {
    return (
      new Intl.DisplayNames(['fr-FR'], { type: 'region' }).of(countryName) ??
      countryName
    );
  } catch {
    return countryName;
  }
};

export const formatAddress = (address: {
  addressComplement?: string | null;
  streetAndNumber: string;
  postCodeCedex: string;
  city: string;
}) =>
  `${address.streetAndNumber}${address.addressComplement ? ` ${address.addressComplement}` : ''} ${address.postCodeCedex} ${address.city}`;

export const getCountryCodeByAlpha3 = (code: string): string | undefined =>
  countries.alpha3ToNumeric(code);

export const formatFullName = (
  user: {
    firstname: string;
    lastname: string;
  },
  options?: { capitalizeLastName?: boolean }
): string => {
  const formattedLastName = options?.capitalizeLastName
    ? user.lastname.toUpperCase()
    : user.lastname;
  return `${user.firstname} ${formattedLastName}`.trim();
};

export const normalizeName = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

export const formatInitials = (user: {
  firstname: string;
  lastname: string;
}): string =>
  `${user.firstname.charAt(0)}${user.lastname.charAt(0)}`.toUpperCase();

export const PHONE_NUMBER_COUNTRY = 'FR';

export const getE164PhoneNumber = (phoneNumber: string, hasSpace = false) => {
  const parsedPhoneNumber = parsePhoneNumberFromString(
    phoneNumber,
    PHONE_NUMBER_COUNTRY,
    metadata
  );
  if (!parsedPhoneNumber || !parsedPhoneNumber.isValid()) {
    return undefined;
  }
  return hasSpace
    ? parsedPhoneNumber.formatInternational()
    : parsedPhoneNumber.formatInternational().replaceAll(' ', '');
};

export const getNationalPhoneNumber = (phoneNumber: string) => {
  const parsedPhoneNumber = parsePhoneNumberFromString(
    phoneNumber,
    PHONE_NUMBER_COUNTRY,
    metadata
  );

  return parsedPhoneNumber?.number || phoneNumber;
};

export const isE164 = (phoneNumber: string): boolean => {
  if (!phoneNumber.startsWith('+')) return false;

  const parsedPhoneNumber = parsePhoneNumberFromString(
    phoneNumber,
    PHONE_NUMBER_COUNTRY,
    metadata
  );

  if (!parsedPhoneNumber || !parsedPhoneNumber.isValid()) return false;

  return parsedPhoneNumber.format('E.164') === phoneNumber;
};

export const isValidMobilePhoneNumber = (phoneNumber: string) => {
  const parsedPhoneNumber = parsePhoneNumberFromString(
    phoneNumber,
    PHONE_NUMBER_COUNTRY,
    metadata
  );

  return (
    parsedPhoneNumber?.isValid() && parsedPhoneNumber.getType() === 'MOBILE'
  );
};

export const validatePhoneNumberForCountries = (
  phoneNumber: string,
  defaultCountry: string,
  allowedCountries: readonly string[]
): string | undefined => {
  const parsed = parsePhoneNumberFromString(
    phoneNumber,
    defaultCountry as Parameters<typeof parsePhoneNumberFromString>[1],
    metadata
  );
  if (!parsed?.isValid()) return undefined;
  if (!parsed.country || !allowedCountries.includes(parsed.country)) {
    return undefined;
  }
  return parsed.format('E.164');
};

type AppendPrefix<
  T extends string,
  P extends string | undefined,
> = P extends string ? `${P}.${T}` : T;

export const appendPrefix = <T extends string, P extends string | undefined>(
  name: T,
  prefix?: P
) => (prefix ? (`${prefix}.${name}` as AppendPrefix<T, P>) : (name as T));

type AssertString = (value: unknown, error?: string) => asserts value is string;

export const assertString: AssertString = (value, error) => {
  const result = z.string().safeParse(value);
  if (!result.success) {
    throw new Error(error ?? 'String required');
  }
};
